import {useState} from 'react';
import {useLocation} from 'react-router-dom';
import {Grid, Stack} from '@mantine/core';
import {
    TabbedComponent,
    TabComponentTab,
} from '@flexinet/ui-components';

import {CustomerSubscription} from '../types';
import {InformationPropertyCard} from './InformationPropertyCard.tsx';
import {MetadataPropertyCard} from './MetadataPropertyCard.tsx';
import {IndexPage as SubscriptionItemsPage} from '../../subscription-items/pages/IndexPage.tsx';
import {IndexPage as InvoicesPage} from '../../invoices/pages/IndexPage.tsx';
import {IndexPage as ActivityLogsPage} from '../../activity-logs/pages/IndexPage.tsx';

const getActiveTabFromHash = (hash: string): string | null => {
    const match = hash.match(/^#tab=(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
};

export const Profile = ({record}: { record: CustomerSubscription, onRecordUpdate?: (updates: Partial<CustomerSubscription>) => void }) => {
    const [currentRecord] = useState(record);
    const location = useLocation();
    const activeTab = getActiveTabFromHash(location.hash);

    const tabDefinitions: { title: string, component: JSX.Element }[] = [
        {
            title: 'Items',
            component: <SubscriptionItemsPage subscriptionId={currentRecord.id}/>,
        },
        {
            title: 'Invoices',
            component: <InvoicesPage subscriptionId={currentRecord.id}/>,
        },
        {
            title: 'Activity',
            // FNP-328: ActivityLogs filtered by this subscription's id.
            // Backend pushes recordId / recordType into the listActivityLogs
            // filter chain — the global /activity-logs index page renders the
            // same module with no props for the unfiltered view.
            component: (
                <ActivityLogsPage
                    recordId={currentRecord.id}
                    recordType="CustomerSubscription"
                />
            ),
        },
    ];

    const tabs = tabDefinitions.map(tab => ({
        ...tab,
        active: activeTab ? tab.title === activeTab : undefined,
    })) as TabComponentTab[];

    return (
        <Grid>
            <Grid.Col span={{base: 12, lg: 4}}>
                <Stack gap={'xl'}>
                    <InformationPropertyCard record={currentRecord}/>
                    <MetadataPropertyCard record={currentRecord}/>
                </Stack>
            </Grid.Col>
            <Grid.Col span={{base: 12, lg: 8}}>
                <TabbedComponent key={activeTab ?? 'default'} tabs={tabs}/>
            </Grid.Col>
        </Grid>
    );
};
